import React from 'react';
import { QBox, QCheckbox, QFlex, QIcon, QStack, QTag, QText, QTooltip } from '@qualio/ui-components';
import { CCWorkflowLink, isArchivedWorkflow } from '../../types/ccWorkflow';
import { useTranslation } from 'react-i18next';

type WorkflowCardProps = {
  workflow: CCWorkflowLink;
  isSelected: boolean;
  isEditing: boolean;
  onSelect: (checked: boolean, workflow: CCWorkflowLink) => void;
};

export const WorkflowCard: React.FC<WorkflowCardProps> = ({ workflow, isEditing, isSelected, onSelect }) => {
  const { t } = useTranslation();
  const systemTemplate = workflow.qualio_managed;
  const isArchived = isArchivedWorkflow(workflow);

  const isNonInteractive = !!(systemTemplate || isArchived);
  const cardBg = isArchived ? 'gray.50' : systemTemplate ? 'blue.50' : 'inherit';
  const cardOpacity = !isArchived && isEditing ? 1 : 0.5;

  const handleToggle = () => {
    if (isNonInteractive) return;
    onSelect(!isSelected, workflow);
  };

  return (
    <QBox
      data-cy={`workflow-card-${workflow.id}`}
      as={isEditing ? 'button' : 'div'}
      border="2px solid"
      borderColor={isSelected && !isArchived ? 'blue.200' : 'gray.200'}
      borderRadius="md"
      bg={cardBg}
      p={4}
      width="75%"
      opacity={cardOpacity}
      onClick={handleToggle}
      cursor={isNonInteractive ? 'not-allowed' : 'pointer'}
    >
      <QFlex alignItems="flex-start">
        {!isArchived && (
          <span style={{ marginTop: '4px', marginRight: '12px', pointerEvents: 'none' }}>
            <QCheckbox isChecked={isSelected} isDisabled={systemTemplate} />
          </span>
        )}
        <QStack spacing={1} textAlign="left">
          <QFlex alignItems="center" gap={2}>
            <QText fontSize="md">{workflow.name}</QText>
            {systemTemplate && (
              <QTooltip label={t('template.ccSettings.systemManaged.description')} placement="right">
                <QTag variantColor="blue" data-cy="system-managed-tag">
                  <QFlex alignItems="center" gap={1}>
                    {t('template.ccSettings.systemManaged.badge')}
                    <QIcon iconName="Info" size="xs" data-cy="system-managed-info-icon" />
                  </QFlex>
                </QTag>
              </QTooltip>
            )}
            {isArchived && (
              <QTag variantColor="red" data-cy="archived-tag">
                {t('template.ccSettings.archived.badge')}
              </QTag>
            )}
          </QFlex>
          {workflow.description && (
            <QText fontSize="sm" color="gray.500">
              {workflow.description}
            </QText>
          )}
        </QStack>
      </QFlex>
    </QBox>
  );
};
